import { CURRENCY_SYMBOLS } from "@/constants/currencies";
import {
  DEFAULT_LANGUAGE,
  type ISO_LANGUAGE_CODE,
  ISO_LANGUAGE_CODES,
} from "@/constants/languages";

export const formatCurrency = (value: number | string, currency = "EUR") => {
  const valueAsNumber =
    typeof value === "string" ? parseStringToNumber(value) : value;
  return new Intl.NumberFormat("it-IT", {
    style: "currency",
    currency,
    compactDisplay: "short",
    minimumFractionDigits: 2,
  }).format(valueAsNumber);
};

const alphabethPattern = /[a-zA-Z]/g;

export const INVALID_NUMERIC_INPUTS = [
  ``, // empty string
  `.`, // single dot
  `,`, // single comma
  `..`, // double dots
  `,,`, // double commas
  `...`, // triple dots
  `,,,`, // triple commas
  `.,.`, // mixed separators
  `.,`, // mixed separators
  `,.`, // mixed separators
  `,.,`, // mixed separators
] as const;

const cleanInput = (value?: string | number) => {
  if (!value) {
    return ``;
  }
  const valueAsString = typeof value === "number" ? value.toString() : value;
  const currencySymbolsPattern = new RegExp(
    `[${CURRENCY_SYMBOLS.join(``)}]`,
    `g`,
  );
  const parsed = valueAsString
    .replace(currencySymbolsPattern, ``)
    .replace(alphabethPattern, ``)
    .replace(/[%]/, ``)
    .replace(/[" "]/g, ``)
    .trim();

  return parsed;
};

export const handleMixedSeparators = (value: string) => {
  const parts = value.split(/[,.]/);

  // If we have exactly 3 parts, assume the last one is decimal
  if (parts.length === 3) {
    // Join all parts except last, then add decimal point and last part
    return `${parts.slice(0, -1).join(``)}.${parts[parts.length - 1]}`;
  }

  // If we have more than 3 parts, handle as a special case
  if (parts.length > 3) {
    // Get the last part as decimal and join the rest
    const decimalPart = parts.pop();
    return `${parts.join(``)}.${decimalPart}`;
  }
  if (parts.length === 2) {
    // For two parts, assume the last one is decimal
    return `${parts[0]}.${parts[1]}`;
  }

  // For single part or empty string, return the value as is
  return parts.join(``);
};

const processDecimalPart = (value: string) => {
  // Handle regular thousand separators vs decimal separators
  let processedValue = value;
  if (value.includes(`,`)) {
    const parts = value.split(`,`);
    // If the part after comma has exactly 2 digits, treat comma as decimal separator
    if (parts[1]?.length <= 2) {
      processedValue = value.replace(`,`, `.`);
    } else {
      // Otherwise, remove the comma (thousand separator)
      processedValue = value.replace(/,/g, ``);
    }
  }

  // Handle multiple decimal points
  const split = processedValue.split(`.`);
  const parsedHas2Commas = split.length > 1;
  if (parsedHas2Commas) {
    const decimalPart = split.pop();
    return `${split.join(``)}.${decimalPart}`;
  }
  if (split.length === 2) {
    if (split[1].length <= 3) {
      return `${split[0]}.${split[1]}`;
    }
  }

  return processedValue;
};

export const parseInputValue = (value?: string) => {
  if (!value) {
    return ``;
  }
  const parsed = cleanInput(value);

  // Early return for invalid inputs
  if (
    INVALID_NUMERIC_INPUTS.includes(
      parsed as (typeof INVALID_NUMERIC_INPUTS)[number],
    )
  ) {
    return ``;
  }

  // First, handle the case where we have mixed separators
  if (parsed.includes(`,`) && parsed.includes(`.`)) {
    return handleMixedSeparators(parsed);
  }

  // Handle regular thousand separators vs decimal separators
  const processedValue = processDecimalPart(parsed);

  return processedValue;
};

export const parseStringToNumber = (value: string) => {
  return Number.parseFloat(parseInputValue(value));
};

export const formatNumber = (
  value: number | string,
  options: Intl.NumberFormatOptions & {
    language?: ISO_LANGUAGE_CODE;
  },
) => {
  const {
    currency,
    style,
    language,
    maximumFractionDigits,
    minimumFractionDigits,
    ...rest
  } = options;
  const parsedValue =
    typeof value === "string" ? Number.parseFloat(value) : value;
  let valueNumber = Number.isNaN(parsedValue) ? 0 : parsedValue;
  let minFractionDigits = minimumFractionDigits ?? 1;
  let defaultCurrency = `EUR`;
  if (style === `currency`) {
    minFractionDigits = minimumFractionDigits ?? 2;
    defaultCurrency = `EUR`;
  }
  if (style === `percent`) {
    valueNumber /= 100;
    minFractionDigits = minimumFractionDigits ?? 1;
  }
  const formatter = new Intl.NumberFormat(
    language ?? ISO_LANGUAGE_CODES[DEFAULT_LANGUAGE],
    {
      style: style ?? `decimal`,
      currency: currency ?? defaultCurrency,
      compactDisplay: `short`,
      minimumFractionDigits: minFractionDigits,
      maximumFractionDigits: maximumFractionDigits ?? 2,
      ...rest,
    },
  );
  return formatter.format(valueNumber);
};

// Italian literal — small targeted exception to the no-`t`-in-`.ts` rule
// kept in the formatter for simplicity. If more locales land, lift to i18n.
const UNSPECIFIED_IT = "Non specificato";

export function formatJobCompensation(job: {
  payment_type?: string;
  daily_rate_min?: number | string | null;
  daily_rate_max?: number | string | null;
  budget?: number | null;
  currency?: string | null;
}): string {
  const currency = job.currency || "EUR";

  if (job.payment_type === "fixed" && job.budget) {
    return `${formatNumber(job.budget, { style: "currency", currency })}`;
  }

  const min = toNumberOrNull(job.daily_rate_min);
  const max = toNumberOrNull(job.daily_rate_max);

  if (min != null && max != null) {
    return `${formatNumber(min, { style: "currency", currency })} - ${formatNumber(max, { style: "currency", currency })}`;
  }

  return UNSPECIFIED_IT;
}

export function formatDailyRate(
  value: string | number | null | undefined,
  currency = "EUR",
): string {
  const num = toNumberOrNull(value);
  if (num == null) return UNSPECIFIED_IT;
  return `${formatNumber(num, { style: "currency", currency })}/g`;
}

const toNumberOrNull = (
  value: number | string | null | undefined,
): number | null => {
  if (value == null || value === "") return null;
  const parsed = typeof value === "string" ? parseStringToNumber(value) : value;
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

type PercentageBreakdown = {
  totalAmount: number;
  percentage: number;
  calculatedAmount: number;
  remainingPercentage: number;
  remainingAmount: number;
};

/**
 * Calculates percentage breakdown from a total amount and percentage
 * @param totalAmount - The total amount
 * @param percentage - The percentage value (e.g., 30 for 30%)
 * @returns An object containing total, percentage amount, remaining percentage, and remaining amount
 */
export function calculatePercentageBreakdown(
  totalAmount: number,
  percentage: number,
): PercentageBreakdown {
  const calculatedAmount = totalAmount * (percentage / 100);
  const remainingPercentage = 100 - percentage;
  const remainingAmount = totalAmount - calculatedAmount;

  return {
    totalAmount,
    percentage,
    calculatedAmount,
    remainingPercentage,
    remainingAmount,
  };
}
/**
 *
 * @param array - [{ code: "en", level: "Professionale" }, { code: "it", level: "Madrelingua" }]
 * @returns { en: "Professionale", it: "Madrelingua" }
 */
export const reduceLanguagesArrayToObject = (
  array: {
    code: string;
    level: string;
  }[],
) => {
  return array.reduce(
    (acc, item) => {
      acc[item.code] = item.level;
      return acc;
    },
    {} as Record<string, string>,
  );
};

/**
 * 
 * @param object - {
  "en": "Professionale",
  "it": "Madrelingua"
}
 * @returns [{ code: "en", level: "Professionale" }, { code: "it", level: "Madrelingua" }]
 */
export const parseLanguagesToArray = (object: Record<string, string>) => {
  return Object.keys(object).map((key) => ({
    code: key,
    level: object[key],
  }));
};

/** "Marco Bianchi" → "MB". Falls back to the empty string for blank names. */
export const getInitials = (fullName: string): string =>
  fullName
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");

const HOURS_PER_DAY = 8;

export const convertDailyToHourly = (daily: number): number =>
  daily / HOURS_PER_DAY;
